import { createContext } from "@codesandbox/context";
import type {
  AbsoluteWorkspacePath,
  RelativeWorkspacePath,
} from "@codesandbox/pitcher-client";
import { useActiveCodeEditor } from "components/Layout/Panels/VSCode/utils";
import type { EditorRouter } from "features/types/router";
import { usePitcher } from "features/utils/usePitcher";
import type { IRange } from "monaco-editor";
import { useEffect, useState } from "react";

/**
 * Supports the format 5,0 and 5,0-6,5
 */
const convertSelectionToRange = (selection: string) => {
  try {
    const [startRange, endRange] = selection
      .split("-")
      .map((range) => range.split(",").map(Number));

    const range: IRange = {
      startLineNumber: startRange[0],
      startColumn: startRange[1],
      endLineNumber: endRange?.[0] ?? startRange[0],
      endColumn: endRange?.[1] ?? startRange[1],
    };

    return range;
  } catch {
    return;
  }
};

const convertRangeToSelection = (range?: IRange) => {
  if (!range) {
    return "";
  }

  if (
    range.startLineNumber === range.endLineNumber &&
    range.startColumn === range.endColumn
  ) {
    return `:${range.startLineNumber},${range.startColumn}`;
  }

  return `:${range.startLineNumber},${range.startColumn}-${range.endLineNumber},${range.endColumn}`;
};

/**
 * Handles what is considered the active file. This is based on any file tab opened or changed to. It
 * consists of a path and a selection
 */
export const useActiveFile = createContext(
  ({ router }: { router: EditorRouter }) => {
    const pitcher = usePitcher();
    const activeEditor = useActiveCodeEditor();

    const [state, setState] = useState<
      | {
          path: RelativeWorkspacePath;
          initialSelection?: IRange;
        }
      | undefined
    >(() => {
      const activeFile = router.queryParams.file.get();

      // We are repurposing an existing query param and want to ignore it when it has the old format
      if (activeFile?.startsWith("%")) {
        return;
      }

      if (activeFile) {
        const [path, selection] = activeFile.split(":");

        return {
          path: pitcher.clients.fs.asRelativeWorkspacePath(path),
          initialSelection: selection
            ? convertSelectionToRange(selection)
            : undefined,
        };
      }
    });

    const setter = (path: RelativeWorkspacePath, selection?: IRange) => {
      router.queryParams.file.set(
        `${path}${convertRangeToSelection(selection)}`,
      );

      const shouldSetState = path !== state?.path || !selection;

      if (!shouldSetState) {
        return;
      }

      setState({
        path: pitcher.clients.fs.asRelativeWorkspacePath(path),
      });
    };

    const unsetter = () => {
      setState(undefined);
      router.queryParams.file.delete();
    };

    useEffect(() => {
      if (!activeEditor) {
        return;
      }

      const unsubs = activeEditor.onDidChangeModel(({ newModelUrl }) => {
        const path = newModelUrl?.path;

        if (!path) {
          return;
        }

        const relativePath = pitcher.clients.fs.absoluteToRelativeWorkspacePath(
          path as AbsoluteWorkspacePath,
        );

        setter(relativePath);
      });

      return () => {
        unsubs?.dispose();
      };
    }, [activeEditor]);

    return [state, { set: setter, unset: unsetter }] as const;
  },
);
